import type { UserGeo } from '#shared/utils/resolveUserGeo'

/**
 * Typesense geo ranking primitives (KFT-4116).
 *
 * Typesense has no IP-geo of its own (unlike Algolia's `aroundLatLngViaIP`), so
 * proximity ranking needs explicit coordinates — resolved client-side via
 * `useUserGeo()` (`/api/geo`, KFT-4027) — turned into a distance `sort_by`.
 *
 * Single source of truth for the geopoint field name and the sort clause shape,
 * shared by every surface (listing, localities, homepage/promo/review showcases,
 * federated autocomplete) and the federated search lab.
 */

export interface GeoCoords {
  lat: number
  lng: number
}

/**
 * Typesense geopoint field to distance-sort on. Mirrors the Algolia `_geoloc`
 * source field (`app/composables/types/searchEngine.ts`). Confirmed as the events /
 * locations collection field for the KFT-4024 cluster — change here only if the
 * cluster schema field is renamed.
 */
export const TYPESENSE_GEO_FIELD = '_geoloc'

/**
 * Distance bucket (km) for geo sorting. Typesense treats every result within
 * this radius as equidistant, so the tiebreaker term (opt_score, event_count,
 * text match, …) orders nearby results rather than raw metre-level proximity
 * dominating — the Typesense equivalent of Algolia's `aroundPrecision`.
 */
export const GEO_SORT_PRECISION_KM = 10

/**
 * Typesense rejects a search with more than 3 `sort_by` fields ("Only upto 3
 * sort fields are allowed"). Algolia's ranking chain has no such limit, so a
 * ported customRanking (plus a geo lead) can overflow — hence the cap.
 */
export const TYPESENSE_MAX_SORT_FIELDS = 3

/**
 * Split a `sort_by` on its top-level commas only, leaving commas inside a
 * clause's parens/brackets intact — e.g. `_geoloc(52.5,13.4, precision: 10km)`
 * and `_eval([(a:=x):2, (b):1])` each stay a single field.
 */
const splitTopLevelSortFields = (sortBy: string): string[] => {
  const fields: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < sortBy.length; i++) {
    const char = sortBy[i]
    if (char === '(' || char === '[') depth++
    else if (char === ')' || char === ']') depth--
    else if (char === ',' && depth === 0) {
      fields.push(sortBy.slice(start, i))
      start = i + 1
    }
  }
  fields.push(sortBy.slice(start))
  return fields.map((f) => f.trim()).filter(Boolean)
}

/**
 * Keep at most `max` sort fields (default 3, Typesense's hard limit). Lower-
 * priority tail fields are dropped — geo + the leading customRanking terms win.
 */
export const capSortFields = (
  sortBy: string,
  max: number = TYPESENSE_MAX_SORT_FIELDS
): string => splitTopLevelSortFields(sortBy).slice(0, max).join(',')

/** 28-day bookings — the trending term of Algolia's live `events_de` customRanking. */
export const TRENDING_SORT = 'trending.28_days:desc'

/** Events tiebreakers: dated events first, then recent bookings. */
export const EVENTS_RANKING = `has_events:desc,${TRENDING_SORT}`

/**
 * Text relevance as one sort field. Buckets group near-equal text scores so the
 * fields after it break ties, the way Algolia's customRanking only orders
 * results its textual criteria can't separate. 3 buckets measured best against
 * Algolia on the 184 head queries (KFT-4031).
 */
export const TEXT_MATCH_SORT = '_text_match(buckets: 3):desc'

/**
 * Text scoring for event searches, closest to Algolia's `events_de` ranking
 * (KFT-4031): score by the highest-priority matching field (Algolia's
 * `attribute` criterion) and prefer query words early in the text.
 */
export const EVENTS_TEXT_MATCH_PARAMS = {
  text_match_type: 'max_weight',
  prioritize_token_position: true,
} as const

/**
 * Radius (km) that counts as "nearby" for the federated events search
 * (KFT-4145). Everything inside it ranks as one group, ordered by relevance.
 */
export const NEARBY_RADIUS_KM = 50

/**
 * Nearby-first `sort_by` for query-driven event search (KFT-4145): events
 * within `NEARBY_RADIUS_KM` first, then online events, then the rest — each
 * group ordered by `tiebreaker` (e.g. `has_events:desc,_text_match:desc`).
 *
 * Unlike `buildTypesenseGeoSort`, relevance survives: a 10km distance bucket
 * followed by popularity ranked every loosely matching nearby event by
 * trending, so "gin probieren" in Berlin led with a wine tasting. In hybrid
 * search `_text_match` is the rank-fusion score, so the tiebreaker keeps both
 * keyword and semantic relevance.
 *
 * Online events carry a placeholder Berlin `_geoloc`, so the nearby group
 * excludes them explicitly — otherwise they'd count as "nearby" for Berlin
 * viewers only.
 */
export const buildTypesenseNearbyFirstSort = (
  coords: GeoCoords | null | undefined,
  tiebreaker: string,
  radiusKm: number = NEARBY_RADIUS_KM
): string | undefined => {
  if (!coords) return undefined
  const nearby = `${TYPESENSE_GEO_FIELD}:(${coords.lat},${coords.lng}, ${radiusKm} km) && is_online:false`
  const groups = `_eval([ (${nearby}):2, (is_online:true):1 ]):desc`
  return capSortFields(`${groups},${tiebreaker}`)
}

/**
 * Distance bucket (km) for browse ranking on /search and /c/ pages — the
 * Typesense equivalent of the `aroundPrecision: 15000` Algolia sends there.
 */
export const BROWSE_GEO_PRECISION_KM = 15

/**
 * `sort_by` for browsing events without text, with viewer coords (KFT-4183):
 * dated offline events within `NEARBY_RADIUS_KM` first, then everything in
 * `BROWSE_GEO_PRECISION_KM` distance buckets, then recent bookings — Algolia's
 * geo-before-customRanking order, with nearby dated events kept on top.
 *
 * Unlike `buildTypesenseNearbyFirstSort`, distance keeps ordering inside the
 * nearby radius, so a busier neighbouring city doesn't outrank the viewer's
 * own. `has_events` ahead of distance was rejected: on thin categories a dated
 * event hundreds of km away then outranked every undated local one.
 *
 * Online events carry a placeholder Berlin `_geoloc`, so (as on Algolia) the
 * distance buckets rank them as near for Berlin viewers only.
 */
export const buildTypesenseBrowseGeoSort = (
  coords: GeoCoords | null | undefined
): string | undefined => {
  if (!coords) return undefined
  const nearbyDated = `${TYPESENSE_GEO_FIELD}:(${coords.lat},${coords.lng}, ${NEARBY_RADIUS_KM} km) && has_events:true && is_online:false`
  return capSortFields(
    `_eval(${nearbyDated}):desc,${buildTypesenseGeoSort(coords, TRENDING_SORT, BROWSE_GEO_PRECISION_KM)}`
  )
}

/**
 * `sortBy` with its distance field swapped for text match (or a nearby-first
 * sort turned back into the text-first chain), or undefined when it has no
 * distance field. Used when a city curation rule fires: the named city
 * then outranks the viewer's location (Algolia's city rule sets
 * `aroundLatLngViaIP: false` for the same reason).
 */
export const replaceGeoSortWithTextMatch = (
  sortBy: string | undefined
): string | undefined => {
  if (!sortBy) return undefined
  const fields = splitTopLevelSortFields(sortBy)
  // Nearby-first or browse sort (`buildTypesenseNearbyFirstSort`,
  // `buildTypesenseBrowseGeoSort`): drop the nearby grouping and any distance
  // field, and fall back to the text-first chain — text match, then the
  // remaining non-ranking tiebreakers, then the events ranking to fill the 3
  // slots.
  const nearbyIndex = fields.findIndex(
    (f) => f.startsWith('_eval(') && f.includes(`${TYPESENSE_GEO_FIELD}:(`)
  )
  if (nearbyIndex !== -1) {
    const eventsRanking = splitTopLevelSortFields(EVENTS_RANKING)
    const rest = fields.filter(
      (f, i) =>
        i !== nearbyIndex &&
        !f.startsWith(`${TYPESENSE_GEO_FIELD}(`) &&
        !eventsRanking.includes(f)
    )
    const chain = [TEXT_MATCH_SORT, ...rest, ...eventsRanking]
    return capSortFields([...new Set(chain)].join(','))
  }
  const geoIndex = fields.findIndex((f) =>
    f.startsWith(`${TYPESENSE_GEO_FIELD}(`)
  )
  if (geoIndex === -1) return undefined
  fields[geoIndex] = TEXT_MATCH_SORT
  return fields.join(',')
}

/**
 * Whether `sortBy` ranks by distance — a `_geoloc(…)` field or a nearby-first
 * `_eval` group, anywhere in the chain. Those carry the viewer's coordinates,
 * which makes the request per-viewer (see `useCache` in typesenseClient).
 */
export const hasGeoSort = (sortBy: string | undefined): boolean =>
  Boolean(sortBy) &&
  splitTopLevelSortFields(sortBy!).some(
    (f) =>
      f.startsWith(`${TYPESENSE_GEO_FIELD}(`) ||
      (f.startsWith('_eval(') && f.includes(`${TYPESENSE_GEO_FIELD}:(`))
  )

/**
 * Narrow a resolved `UserGeo` (or null) to plain finite coordinates, or null.
 * Guards against a partial/garbage geo payload producing a `_geoloc(NaN,…)`
 * clause that Typesense would reject.
 */
export const toGeoCoords = (
  geo: UserGeo | null | undefined
): GeoCoords | null =>
  geo && Number.isFinite(geo.lat) && Number.isFinite(geo.lng)
    ? { lat: geo.lat, lng: geo.lng }
    : null

/**
 * Distance `sort_by` clause — nearest first. Replaces Algolia's
 * `aroundLatLngViaIP` geo bias on the Typesense path. Returns `undefined` when
 * no coordinates are available, so every caller stays a no-op without geo (the
 * graceful `null` fallback every surface must tolerate).
 *
 * `tiebreaker` is appended after the distance term for query-driven search
 * (e.g. `_text_match:desc`, so equidistant docs fall back to text relevance) or
 * a showcase relevancy signal. Omit it for a pure distance sort.
 */
export const buildTypesenseGeoSort = (
  coords: GeoCoords | null | undefined,
  tiebreaker?: string,
  precisionKm: number = GEO_SORT_PRECISION_KM
): string | undefined => {
  if (!coords) return undefined
  // `precision: N km` buckets nearby results as equidistant so `tiebreaker`
  // orders them (Algolia aroundPrecision equivalent) — see GEO_SORT_PRECISION_KM.
  const distance = `${TYPESENSE_GEO_FIELD}(${coords.lat},${coords.lng}, precision: ${precisionKm}km):asc`
  // Geo takes one of Typesense's 3 sort-field slots; cap the combined clause.
  return tiebreaker ? capSortFields(`${distance},${tiebreaker}`) : distance
}
